import React, { useEffect, useState } from "react";
import CloseIcon from '@mui/icons-material/Close';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import PostData from '../../services/PostData.js'
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import InputAdornment from '@mui/material/InputAdornment';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import SnackbarEx from "../Helpers/SnackbarEx.jsx";

const toPositiveInt = (value, fallback = 1) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return fallback;
    return Math.round(n);
};

const toMoneyNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(String(value).replace(/[$,\s]/g, ''));
    return Number.isFinite(n) ? n : null;
};

const hasExtractedMoney = (value) => toMoneyNumber(value) !== null;

/** Match server isMacBidPlatform — show macItemSubId only for MAC.BID invoices. */
const isMacBidSupplier = (name) => {
    const p = String(name || "")
        .toLowerCase()
        .replace(/_/g, ".")
        .replace(/\s+/g, " ")
        .trim();
    if (!p) return false;
    return (
        p === "mac.bid" ||
        p === "mac bid" ||
        p.startsWith("mac.bid") ||
        p.startsWith("mac bid") ||
        p.includes("mac.bid")
    );
};

const TOTAL_FIELDS = [
    { field: 'Subtotal', name: 'subTotal', pdfKey: 'subTotal' },
    { field: 'Shipping', name: 'shipping', pdfKey: 'shipping' },
    { field: 'Sales Tax', name: 'sales_tax', pdfKey: 'sales_tax' },
    { field: 'Total', name: 'total', pdfKey: 'total' },
];

export default function InvoiceResponse({ responseData, closeDialog }) {
    const pdf = responseData?.pdf
    const importConfig = responseData?.importConfig
    const invoiceIdSource = pdf?.invoiceIdSource || importConfig?.invoiceIdSource || "invoice_id"
    const parseDescriptionCount = !!(
        pdf?.parseDescriptionCount
        ?? importConfig?.parseDescriptionCount
    )
    const supplierDisplay =
        pdf?.supplierDisplayName
        || importConfig?.supplierDisplayName
        || importConfig?.sourceName
        || pdf?.supplier?.name
        || ''

    const invoiceIdValue = invoiceIdSource === "barcode"
        ? (pdf?.barcode ?? pdf?.invoice_id ?? '')
        : (pdf?.invoice_id ?? '')

    const [submitLoad, setSubmitLoad] = useState(false)
    const [successUpload, setSuccessUpload] = useState(false)
    const [nextParse, setNextParse] = useState(false)
    const [attachPrompt, setAttachPrompt] = useState({ open: false, items: [] })

    const [openTestSnack, setOpenTestSnack] = useState({
        color: "",
        message: "",
        open: false
    })

    const stagingId = pdf?.invoiceStagingId || pdf?.invoiceDriveName || "";

    const [inputVals, setInputVals] = useState([
        { field: "Invoice ID", data: invoiceIdValue, name: "invoice_id", required: true },
        { field: "Supplier Name", data: supplierDisplay, name: "supplier_name", required: true },
        { field: "Date", data: pdf?.invoice_date ?? '', name: "invoice_date", required: true },
        // Hidden: server staging id for PDF bytes (not a Drive file)
        { field: "Invoice Staging ID", data: stagingId, name: "invoice_staging_id", required: true, hidden: true },
        { field: "Supplier Address", data: pdf?.supplier?.address ?? '', name: "supplier_address", required: false },
        ...TOTAL_FIELDS
            .filter(({ pdfKey }) => hasExtractedMoney(pdf?.[pdfKey]))
            .map(({ field, name, pdfKey }) => ({
                field,
                data: String(toMoneyNumber(pdf?.[pdfKey])),
                name,
                required: false,
                isMoney: true,
            })),
    ])

    const [errors, setErrors] = useState({});

    const liveInvoiceId = String(
        inputVals?.find((v) => v.name === "invoice_id")?.data ?? invoiceIdValue
    ).trim();
    const liveSupplierName = String(
        inputVals?.find((v) => v.name === "supplier_name")?.data ?? supplierDisplay
    ).trim();
    const duplicateStillActive =
        Boolean(pdf?.invoiceAlreadyExists) &&
        liveInvoiceId === String(invoiceIdValue || "").trim();
    const macDuplicate = isMacBidSupplier(liveSupplierName) || isMacBidSupplier(supplierDisplay);
    const existingSupplierLabel =
        pdf?.existingInvoiceSupplier || supplierDisplay || "MAC.BID";

    useEffect(() => {
        if (pdf?.invoiceAlreadyExists) {
            setOpenTestSnack({
                color: "warning",
                message: macDuplicate
                    ? `MAC invoice ${invoiceIdValue || ""} already exists${
                          pdf?.existingInvoiceSupplier
                              ? ` under ${pdf.existingInvoiceSupplier}`
                              : ""
                      }. Confirm is blocked — nothing will be saved.`
                    : `Invoice ${invoiceIdValue || ""} already exists for ${
                          supplierDisplay || "this supplier"
                      }. Confirm will be rejected.`,
                open: true,
            });
        }
    }, [
        pdf?.invoiceAlreadyExists,
        pdf?.existingInvoiceSupplier,
        invoiceIdValue,
        supplierDisplay,
        macDuplicate,
    ]);

    const [inputItems, setInputItems] = useState(pdf?.items?.map(item => {
        const qty = toPositiveInt(item?.qty, 1);
        const unitsPerCase = parseDescriptionCount
            ? toPositiveInt(item?.units_per_case, 1)
            : 1;
        const lineTotal = toMoneyNumber(item?.total)
            ?? (toMoneyNumber(item?.unit_price) != null
                ? toMoneyNumber(item.unit_price) * qty
                : 0);
        return {
            description: item?.description ?? '',
            item_number: item?.item_number ?? '',
            qty,
            units_per_case: unitsPerCase,
            price: lineTotal,
            auctionLink: item?.auctionLink ?? item?.auction_link ?? '',
            macItemSubId: item?.macItemSubId ?? '',
        };
    }) || [])

    const supplierNameLive =
        inputVals.find((v) => v.name === "supplier_name")?.data || supplierDisplay;
    const showMacItemSubId = isMacBidSupplier(supplierNameLive);

    const handleInputChanges = (e, fieldName) => {
        const { value } = e.target;
        setInputVals(prevVals => prevVals.map(val =>
            val.name === fieldName ? { ...val, data: value } : val
        ));
        if (value) {
            setErrors(prevErrors => ({ ...prevErrors, [fieldName]: '' }));
        }
    }

    const handleItemInputChanges = (e, index, fieldName) => {
        const { value } = e.target;
        setInputItems(prevItems => {
            const newItems = [...prevItems];
            newItems[index] = { ...newItems[index], [fieldName]: value };
            return newItems;
        });
    };

    const moneyHeader = (name) => {
        const row = inputVals.find((v) => v.name === name);
        return row ? toMoneyNumber(row.data) : null;
    };

    const shippingAmt = moneyHeader('shipping') ?? 0;
    const taxAmt = moneyHeader('sales_tax') ?? 0;
    const extrasTotal = shippingAmt + taxAmt;
    const merchandiseTotal = inputItems.reduce((sum, item) => {
        const line = toMoneyNumber(item.price) ?? 0;
        return sum + line;
    }, 0);

    const unitBaseCostForItem = (item) => {
        const lineQty = toPositiveInt(item.qty, 1);
        const packCount = parseDescriptionCount ? toPositiveInt(item.units_per_case, 1) : 1;
        const effectiveQty = lineQty * packCount;
        const lineTotal = toMoneyNumber(item.price) ?? 0;
        const share = merchandiseTotal > 0 ? lineTotal / merchandiseTotal : 0;
        const landed = lineTotal + extrasTotal * share;
        return effectiveQty > 0 ? landed / effectiveQty : 0;
    };

    const buildSubmitPayload = (attachExistingWithoutInvoice = false) => {
        const itemsForSubmit = inputItems.map((item) => {
            const qty = toPositiveInt(item.qty, 1);
            const unitsPerCase = parseDescriptionCount
                ? toPositiveInt(item.units_per_case, 1)
                : 1;
            const payload = {
                description: item.description,
                item_number: item.item_number,
                // Line merchandise total — server divides by qty (+ allocates shipping/tax)
                price: toMoneyNumber(item.price) ?? 0,
                units_per_case: unitsPerCase,
                qty: qty * unitsPerCase,
                auctionLink: String(item.auctionLink || "").trim(),
            };
            if (showMacItemSubId) {
                const macItemSubId = String(item.macItemSubId || "").trim();
                if (macItemSubId) payload.macItemSubId = macItemSubId;
            }
            return payload;
        });
        return {
            headers: inputVals,
            items: itemsForSubmit,
            attachExistingWithoutInvoice,
        };
    };

    const submitInvoice = async (attachExistingWithoutInvoice = false) => {
        if (duplicateStillActive) {
            setOpenTestSnack({
                color: "warning",
                message: macDuplicate
                    ? `MAC invoice ${liveInvoiceId || invoiceIdValue || ""} already exists${
                          existingSupplierLabel ? ` under ${existingSupplierLabel}` : ""
                      }. Nothing was saved to GCS or MongoDB.`
                    : "This supplier + invoice number already exists. Nothing was saved.",
                open: true,
            });
            return;
        }

        setSubmitLoad(true)
        const submitVal = buildSubmitPayload(attachExistingWithoutInvoice)
        const { success, error } = await PostData(submitVal, "broylesllc/confirm-invoice-data", null)
        setSubmitLoad(false)

        if (error?.response?.status === 409) {
            const data = error.response.data || {}
            if (data.code === "existing_catalog_without_invoice") {
                setAttachPrompt({
                    open: true,
                    items: Array.isArray(data.items) ? data.items : [],
                })
                return
            }
            const existingUnder = data.existingInvoiceSupplier
                ? ` under ${data.existingInvoiceSupplier}`
                : "";
            setOpenTestSnack({
                color: "warning",
                message: isMacBidSupplier(liveSupplierName)
                    ? `MAC invoice already exists${existingUnder}. Nothing was saved to GCS or MongoDB.`
                    : "This supplier + invoice number already exists. Nothing was saved.",
                open: true,
            });
            if (responseData.type === "batch" && responseData.invoiceCount > 0) {
                setSuccessUpload(true)
                setNextParse(true)
            } else {
                setSuccessUpload(true)
                setTimeout(() => closeDialog("Exists"), 800)
            }
            return
        }

        if (error?.response?.status === 400) {
            setOpenTestSnack({
                color: "error",
                message:
                    error?.response?.data?.message ||
                    "Could not save invoice. Re-upload the PDF and try again.",
                open: true,
            });
            return
        }

        if (success) {
            setAttachPrompt({ open: false, items: [] })
            if (responseData.type === "batch" && responseData.invoiceCount > 0) {
                setSuccessUpload(true)
                setNextParse(true)
            } else {
                setTimeout(() => closeDialog("Success"), 800)
            }
        }
    };

    const handleSubmit = async () => {
        const newErrors = {};
        let hasError = false;

        inputVals.forEach(val => {
            if (val.required && !val.data) {
                newErrors[val.name] = `${val.field} cannot be blank`;
                hasError = true;
            }
        });

        const missingItemNumber = inputItems.some(
            (item) => !String(item.item_number || "").trim()
        );
        if (missingItemNumber) {
            setOpenTestSnack({
                color: "error",
                message: "Each line needs an Item Number (used as Parser Item ID).",
                open: true,
            });
            return;
        }

        if (hasError) {
            setErrors(newErrors);
            return;
        }

        await submitInvoice(false)
    }

    const handleClose = () => closeDialog()

    const headerFields = inputVals.filter(
        (val) => !val.hidden && val.name !== "invoice_drive_id" && !val.isMoney
    )
    const totalFields = inputVals.filter((val) => val.isMoney)

    return (
        <div style={{
            height: '90vh', display: 'flex',
            flexDirection: 'column', overflowX: "hidden"
        }}>
            <div className='row' style={{
                width: "100%", padding: "20px 0 10px 10px", position: "sticky",
                top: 0, zIndex: 100, background: "white",
                borderBottom: 'solid lightgray', margin: 0
            }}>
                <div className='col-9'>
                    <h4 style={{ marginBottom: "0" }}>Invoice Confirmation: </h4>
                    {parseDescriptionCount && (
                        <Typography variant="caption" color="text.secondary">
                            Pack counts enabled — inventory units = line qty × count in description
                        </Typography>
                    )}
                </div>
                <div>
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            position: 'absolute', width: '40px', height: '40px', top: '10px', right: '30px',
                            boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
                            backgroundColor: 'white', zIndex: 100, "&:hover": { backgroundColor: '#f0f0f0' }
                        }}
                    >
                        <CloseIcon sx={{ cursor: 'pointer' }} />
                    </IconButton>
                </div>
            </div>
            <div style={{ padding: '0 10px', overflowY: 'auto', flex: 1 }}>
                {duplicateStillActive && (
                    <Alert severity="warning" sx={{ mt: 1.5, mb: 1 }}>
                        {macDuplicate ? (
                            <>
                                MAC invoice <strong>{liveInvoiceId || invoiceIdValue || "—"}</strong>{" "}
                                already exists
                                {pdf?.existingInvoiceSupplier
                                    ? <> under <strong>{pdf.existingInvoiceSupplier}</strong></>
                                    : null}
                                . Duplicate MAC invoice numbers are not allowed across any MAC.BID
                                location. Confirm is blocked — nothing will be written to GCS or
                                MongoDB.
                            </>
                        ) : (
                            <>
                                Invoice {invoiceIdValue || "—"} already exists for{" "}
                                {supplierDisplay || "this supplier"}. Confirm will be rejected
                                unless you change the supplier or invoice number.
                            </>
                        )}
                    </Alert>
                )}
                <div>
                    {headerFields.map((val, ind) =>
                        <div className='row m-0' key={ind}>
                            <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>{val.field}</label>
                            <TextField
                                size="small"
                                placeholder={val.name === "invoice_date" ? "mm/dd/YYYY" : ""}
                                name={val.name}
                                value={val.data}
                                onChange={(e) => handleInputChanges(e, val.name)}
                                sx={{ width: '100%' }}
                                error={!!errors[val.name]}
                                helperText={errors[val.name]}
                            />
                        </div>
                    )}
                </div>
                {totalFields.length > 0 && (
                    <div style={{
                        border: '1px solid #e0e0e0',
                        padding: '10px',
                        marginTop: '12px',
                        borderRadius: '5px',
                        background: '#fafafa',
                    }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Invoice totals</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            Shipping and tax are allocated across line items into per-unit Base Cost
                        </Typography>
                        {totalFields.map((val) =>
                            <div className='row m-0' key={val.name}>
                                <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>{val.field}</label>
                                <TextField
                                    size="small"
                                    name={val.name}
                                    value={val.data}
                                    onChange={(e) => handleInputChanges(e, val.name)}
                                    sx={{ width: '100%' }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">$</InputAdornment>
                                        ),
                                    }}
                                />
                            </div>
                        )}
                    </div>
                )}
                <div>
                    {inputItems.map((item, itemIndex) => {
                        const lineQty = toPositiveInt(item.qty, 1);
                        const packCount = toPositiveInt(item.units_per_case, 1);
                        const effectiveQty = lineQty * packCount;
                        const unitCost = unitBaseCostForItem(item);
                        return (
                            <div key={itemIndex} style={{ border: '1px solid #e0e0e0', padding: '10px', marginBottom: '10px', marginTop: '10px', borderRadius: '5px' }}>
                                <h5 style={{ marginBottom: '10px' }}>Item {itemIndex + 1}</h5>
                                <div className='row m-0'>
                                    <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>Name</label>
                                    <TextField
                                        size="small"
                                        name='description'
                                        value={item.description}
                                        onChange={(e) => handleItemInputChanges(e, itemIndex, 'description')}
                                        sx={{ width: '100%' }}
                                    />
                                </div>
                                <div className='row m-0'>
                                    <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>Item Number</label>
                                    <TextField
                                        size="small"
                                        name='item_number'
                                        value={item.item_number}
                                        onChange={(e) => handleItemInputChanges(e, itemIndex, 'item_number')}
                                        sx={{ width: '100%' }}
                                    />
                                </div>
                                {showMacItemSubId && (
                                    <div className='row m-0'>
                                        <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>
                                            MAC Item Sub ID
                                        </label>
                                        <TextField
                                            size="small"
                                            name='macItemSubId'
                                            value={item.macItemSubId}
                                            onChange={(e) => handleItemInputChanges(e, itemIndex, 'macItemSubId')}
                                            sx={{ width: '100%' }}
                                        />
                                    </div>
                                )}
                                <div className='row m-0'>
                                    <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>Auction Link</label>
                                    <TextField
                                        size="small"
                                        name='auctionLink'
                                        type='url'
                                        placeholder='https://'
                                        value={item.auctionLink}
                                        onChange={(e) => handleItemInputChanges(e, itemIndex, 'auctionLink')}
                                        sx={{ width: '100%' }}
                                    />
                                </div>
                                <div className='row m-0'>
                                    <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>
                                        {parseDescriptionCount ? 'Line qty (packages)' : 'Quantity'}
                                    </label>
                                    <TextField
                                        size="small"
                                        name='qty'
                                        value={item.qty}
                                        onChange={(e) => handleItemInputChanges(e, itemIndex, 'qty')}
                                        sx={{ width: '100%' }}
                                    />
                                </div>
                                {parseDescriptionCount && (
                                    <>
                                        <div className='row m-0'>
                                            <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>
                                                Units per package (from description)
                                            </label>
                                            <TextField
                                                size="small"
                                                name='units_per_case'
                                                value={item.units_per_case}
                                                onChange={(e) => handleItemInputChanges(e, itemIndex, 'units_per_case')}
                                                sx={{ width: '100%' }}
                                            />
                                        </div>
                                        <div className='row m-0'>
                                            <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>
                                                Effective inventory qty
                                            </label>
                                            <TextField
                                                size="small"
                                                value={effectiveQty}
                                                InputProps={{ readOnly: true }}
                                                helperText={`${lineQty} × ${packCount}`}
                                                sx={{ width: '100%' }}
                                            />
                                        </div>
                                    </>
                                )}
                                <div className='row m-0'>
                                    <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>Line total</label>
                                    <TextField
                                        size="small"
                                        name='price'
                                        value={item.price}
                                        onChange={(e) => handleItemInputChanges(e, itemIndex, 'price')}
                                        sx={{ width: '100%' }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">$</InputAdornment>
                                            ),
                                        }}
                                    />
                                </div>
                                <div className='row m-0'>
                                    <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>
                                        Unit Base Cost (Airtable)
                                    </label>
                                    <TextField
                                        size="small"
                                        value={unitCost.toFixed(2)}
                                        InputProps={{
                                            readOnly: true,
                                            startAdornment: (
                                                <InputAdornment position="start">$</InputAdornment>
                                            ),
                                        }}
                                        helperText={
                                            extrasTotal > 0
                                                ? `Line total ÷ ${effectiveQty} units, plus share of shipping/tax`
                                                : `Line total ÷ ${effectiveQty} units`
                                        }
                                        sx={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className='row m-0'
                style={{ position: 'sticky', bottom: 0, backgroundColor: 'white', padding: '10px 10px', borderTop: '1px solid #e0e0e0', justifyContent: 'end' }}>
                {successUpload ?
                    (nextParse && responseData?.invoiceCount > 0 ? <button
                        onClick={() => closeDialog("next")}
                        className='submit'
                        style={{ width: '205px', background: '#049e04', height: '35px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span>Next Invoice</span> <PlayArrowOutlinedIcon fontSize="small" sx={{ color: 'white', marginLeft: '10px' }} />
                    </button>
                        :
                        <button className='submit'
                            style={{ width: '205px', background: '#049e04', height: '35px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span>Success</span> <CheckOutlinedIcon fontSize="small" sx={{ color: 'white', marginLeft: '10px' }} />
                        </button>
                    )
                    :
                    (!submitLoad ? <button
                        className='submit'
                        type='submit'
                        onClick={handleSubmit}
                        disabled={duplicateStillActive}
                        style={{
                            width: '205px',
                            height: '35px',
                            opacity: duplicateStillActive ? 0.55 : 1,
                            cursor: duplicateStillActive ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {duplicateStillActive ? 'Duplicate — blocked' : 'Submit'}
                    </button> :
                        <button className='submit' type='submit'
                            style={{ width: '205px', height: '35px' }}>
                            <CircularProgress size={20} sx={{ color: 'white', marginTop: '5px' }} />
                        </button>)
                }
            </div>
            {openTestSnack.open && <SnackbarEx color={openTestSnack.color} message={openTestSnack.message} open={openTestSnack.open}
                close={() => setOpenTestSnack({ color: "", message: "", open: false })} />}

            <Dialog
                open={attachPrompt.open}
                onClose={() => !submitLoad && setAttachPrompt({ open: false, items: [] })}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    Existing catalog items found
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 1.5, fontSize: 14, color: "#334155" }}>
                        These Item IDs already exist and have no invoice linked.
                        Add this invoice to them instead of creating duplicates?
                    </Typography>
                    <div
                        style={{
                            maxHeight: 240,
                            overflow: "auto",
                            border: "1px solid #e2e8f0",
                            borderRadius: 8,
                            padding: "8px 12px",
                        }}
                    >
                        {(attachPrompt.items || []).map((row) => (
                            <div
                                key={row.catalogId || row.itemId}
                                style={{
                                    padding: "6px 0",
                                    borderBottom: "1px solid #f1f5f9",
                                    fontSize: 13,
                                }}
                            >
                                <strong>{row.itemId}</strong>
                                {row.status ? ` · ${row.status}` : ""}
                                {row.description ? (
                                    <div style={{ color: "#64748b", marginTop: 2 }}>
                                        {row.description}
                                    </div>
                                ) : null}
                            </div>
                        ))}
                    </div>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        disabled={submitLoad}
                        onClick={() => setAttachPrompt({ open: false, items: [] })}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={submitLoad}
                        onClick={() => submitInvoice(true)}
                        sx={{ textTransform: "none" }}
                    >
                        {submitLoad ? "Saving…" : "Add invoice to existing items"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}
